import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import BadgeMultiSelect from "../../../common/BadgeMultiSelect/BadgeMultiSelect";
import {
  findOption,
  renderImpProductCell,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import { renderReceiptStatusBadge } from "../epsfBadges";
import {
  fetchImpProducts,
  fetchStudyImpKitMaps,
  fetchStudyImpProducts,
  saveStudyImpKitMaps,
  saveStudyImpProduct,
  createReceiveScanSession,
  pollReceiveScanEvents,
  closeReceiveScanSession,
} from "../epsfService";
import {
  formatStorageTempRange,
  formatStrengthDisplay,
} from "./impProductExcel";
import { getStorageTempBadgeTone } from "../epsfTempCompat";
import "../EpsfPages.scss";

const TREATMENT_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "PLACEBO", label: "Placebo" },
  { value: "COMPARATOR", label: "Comparator" },
];

const TREATMENT_FILTER_OPTIONS = [
  ...TREATMENT_OPTIONS,
  { value: "Unassigned", label: "Unassigned" },
];

function getDosageAndStrength(row) {
  const dosage = String(row?.DosageForm || row?.dosageForm || "").trim();
  const strength = formatStrengthDisplay(
    row?.StrengthValue ?? row?.strengthValue,
    row?.StrengthUnit || row?.strengthUnit,
    row?.Strength || row?.strength || "",
  );
  return { dosage, strength };
}

function formatDosageStrengthDisplay(row) {
  const { dosage, strength } = getDosageAndStrength(row);
  return [dosage, strength].filter(Boolean).join(" ");
}

function treatmentTone(value) {
  const key = String(value || "")
    .trim()
    .toUpperCase();
  if (key === "ACTIVE") return "is-active";
  if (key === "PLACEBO") return "is-placebo";
  if (key === "COMPARATOR") return "is-comparator";
  return "is-default";
}

function treatmentLabel(value) {
  const key = String(value || "")
    .trim()
    .toUpperCase();
  if (key === "ACTIVE") return "Active";
  if (key === "PLACEBO") return "Placebo";
  if (key === "COMPARATOR") return "Comparator";
  return String(value || "—");
}

function TreatmentBadge({ value }) {
  return (
    <span
      className={`epsf-setup-badge epsf-setup-badge--treatment ${treatmentTone(value)}`}
    >
      {treatmentLabel(value)}
    </span>
  );
}

function MappingBadge({ value }) {
  const raw = String(value || "").trim();
  const needs =
    !raw ||
    raw === "RequiresSponsorResolution" ||
    /^requires\s+mapping/i.test(raw) ||
    /^unmapped/i.test(raw) ||
    /^not\s+mapped/i.test(raw);
  const label = needs ? "Not mapped" : "IMP mapped";
  return (
    <span
      className={`epsf-setup-badge epsf-setup-badge--mapping ${
        needs ? "is-needs" : "is-mapped"
      }`}
      title={raw || label}
    >
      {label}
    </span>
  );
}

/** Pencil + floating IMP picker (code · name) with optional apply-to-all. */
function KitAssignImpControl({
  value,
  options,
  disabled,
  busy,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState("");
  const [applyAll, setApplyAll] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const locked = disabled || busy;
  const selected = (options || []).find((o) => String(o.value) === String(value));
  const display = selected?.label || "Select IMP";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus?.(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useAnchoredMenuPosition(open, wrapRef, menuRef, setMenuPos, 300, 340);

  const openMenu = (event) => {
    event.stopPropagation();
    if (locked) return;
    setSearch("");
    const anchor = event.currentTarget || wrapRef.current;
    setMenuPos(placePopupNearAnchor(anchor, { width: 300, height: 340 }));
    setOpen((prev) => !prev);
  };

  const q = String(search || "")
    .trim()
    .toLowerCase();
  const filtered = (options || []).filter((opt) => {
    if (!q) return true;
    const hay = `${opt.code || ""} ${opt.name || ""} ${opt.label || ""}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="epsf-kit-assign" ref={wrapRef}>
      <button
        type="button"
        className={`epsf-kit-assign__trigger${selected ? " is-set" : ""}`}
        onClick={openMenu}
        disabled={locked}
        title={busy ? "Saving…" : display}
      >
        <span>{display}</span>
        {busy ? (
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        ) : (
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        )}
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="epsf-kit-assign__menu"
              role="dialog"
              aria-label="Assign IMP"
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="epsf-kit-assign__menu-title">Assign IMP</div>
              <input
                ref={searchRef}
                type="search"
                className="form-control form-control-sm epsf-kit-assign__search"
                placeholder="Search code or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="epsf-kit-assign__list" role="listbox">
                {filtered.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`epsf-kit-assign__item${
                        isSelected ? " is-selected" : ""
                      }`}
                      onClick={() => {
                        setOpen(false);
                        onSelect?.(opt.value, { applyAll });
                      }}
                    >
                      <span className="epsf-kit-assign__item-main">
                        <strong>{opt.code || "—"}</strong>
                        <span>{opt.name || opt.label}</span>
                      </span>
                      {isSelected ? (
                        <i className="fas fa-check" aria-hidden="true" />
                      ) : null}
                    </button>
                  );
                })}
                {!filtered.length ? (
                  <div className="epsf-kit-assign__empty">No matching IMP</div>
                ) : null}
              </div>
              <div className="epsf-kit-assign__footer">
                <button
                  type="button"
                  className="epsf-kit-assign__clear"
                  disabled={!value}
                  onClick={() => {
                    setOpen(false);
                    onSelect?.("", { applyAll });
                  }}
                >
                  Clear selection
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary epsf-kit-assign__apply-all-btn"
                  disabled={!value}
                  title="Assign this IMP to every kit in the batch"
                  onClick={() => {
                    setApplyAll(true);
                    setOpen(false);
                    onSelect?.(value, { applyAll: true });
                  }}
                >
                  Apply to all kits
                </button>
                <label className="epsf-kit-assign__apply-all">
                  <input
                    type="checkbox"
                    checked={applyAll}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setApplyAll(next);
                      // If IMP already selected, checking this applies it to all kits now.
                      if (next && value) {
                        setOpen(false);
                        onSelect?.(value, { applyAll: true });
                      }
                    }}
                  />
                  <span>Use same IMP for all kits?</span>
                </label>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function placePopupNearAnchor(anchorEl, { width = 220, height = 200 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect || (!rect.width && !rect.height)) return { top: 8, left: 8 };
  const gap = 6;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = Math.max(140, Number(width) || 220);
  const menuH = Math.max(100, Number(height) || 200);

  // Prefer directly under the trigger; flip above only when it still sits nearby.
  let left = rect.left;
  let top = rect.bottom + gap;

  if (top + menuH > vh - pad) {
    const above = rect.top - menuH - gap;
    if (above >= pad && above + menuH >= rect.top - 12) {
      top = above;
    } else {
      top = Math.max(pad, vh - pad - menuH);
    }
  }

  // Prefer aligning to the trigger; if overflowing, shift left but keep overlap.
  if (left + menuW > vw - pad) {
    left = Math.max(pad, Math.min(rect.right - menuW, vw - pad - menuW));
  }
  if (left < pad) left = pad;

  top = Math.min(Math.max(pad, top), Math.max(pad, vh - menuH - pad));
  left = Math.min(Math.max(pad, left), Math.max(pad, vw - menuW - pad));
  return { top, left };
}

function useAnchoredMenuPosition(
  open,
  wrapRef,
  menuRef,
  setMenuPos,
  fallbackWidth,
  fallbackHeight,
) {
  useEffect(() => {
    if (!open) return undefined;

    const place = () => {
      const anchor = wrapRef.current;
      const menu = menuRef.current;
      if (!anchor) return;
      const width = menu?.offsetWidth || fallbackWidth;
      const height = menu?.offsetHeight || fallbackHeight;
      setMenuPos(placePopupNearAnchor(anchor, { width, height }));
    };

    const raf = window.requestAnimationFrame(place);
    const onScrollOrResize = () => place();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, wrapRef, menuRef, setMenuPos, fallbackWidth, fallbackHeight]);
}

/** ePlanner-style treatment status pill + floating menu. */
function TreatmentStatusControl({ value, disabled, busy, onChange }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const current = String(value || "")
    .trim()
    .toUpperCase();
  const label = current ? treatmentLabel(current) : "Unassigned";
  const tone = current ? treatmentTone(current) : "is-unassigned";
  const locked = disabled || busy;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useAnchoredMenuPosition(open, wrapRef, menuRef, setMenuPos, 180, 160);

  const openMenu = (event) => {
    event.stopPropagation();
    if (locked) return;
    const anchor = event.currentTarget || wrapRef.current;
    setMenuPos(placePopupNearAnchor(anchor, { width: 180, height: 160 }));
    setOpen((prev) => !prev);
  };

  return (
    <div className="epsf-setup-status" ref={wrapRef}>
      <button
        type="button"
        className={`epsf-setup-status__pill epsf-setup-status__pill--${tone}`}
        onClick={openMenu}
        disabled={locked}
        title={
          busy
            ? "Saving…"
            : disabled
              ? label
              : `${label} — Change treatment`
        }
      >
        <span>{label}</span>
        {busy ? (
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        ) : !disabled ? (
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="epsf-setup-status__menu"
              role="listbox"
              aria-label="Change treatment"
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="epsf-setup-status__menu-title">
                Change treatment
              </div>
              {TREATMENT_OPTIONS.map((option) => {
                const selected = option.value === current;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`epsf-setup-status__menu-item${
                      selected ? " is-selected" : ""
                    }`}
                    onClick={() => {
                      setOpen(false);
                      if (!selected) onChange?.(option.value);
                    }}
                  >
                    <span
                      className={`epsf-setup-status__dot epsf-setup-status__dot--${treatmentTone(
                        option.value,
                      )}`}
                      aria-hidden="true"
                    />
                    <span>{option.label}</span>
                    {selected ? (
                      <i className="fas fa-check" aria-hidden="true" />
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Site-facing label with pencil → small floating editor. */
function SiteFacingLabelEditor({ value, disabled, busy, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(String(value || ""));
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const display = String(value || "").trim() || "—";

  useEffect(() => {
    if (open) setDraft(String(value || ""));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useAnchoredMenuPosition(open, wrapRef, menuRef, setMenuPos, 260, 140);

  const openEditor = (event) => {
    event.stopPropagation();
    if (disabled || busy) return;
    const anchor = event.currentTarget || wrapRef.current;
    setMenuPos(placePopupNearAnchor(anchor, { width: 260, height: 140 }));
    setOpen(true);
  };

  const save = async () => {
    const next = String(draft || "").trim();
    setOpen(false);
    if (next === String(value || "").trim()) return;
    await onSave?.(next);
  };

  return (
    <div className="epsf-setup-label-edit" ref={wrapRef}>
      <span className="epsf-setup-label-edit__text" title={display}>
        {display}
      </span>
      <button
        type="button"
        className="epsf-setup-label-edit__btn"
        onClick={openEditor}
        disabled={disabled || busy}
        title={
          busy
            ? "Saving…"
            : disabled
              ? "Assign a treatment first"
              : "Edit site-facing label"
        }
        aria-label="Edit site-facing label"
      >
        {busy ? (
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        ) : (
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        )}
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="epsf-setup-label-edit__menu"
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="epsf-setup-label-edit__menu-title">
                Site-facing label
              </div>
              <input
                className="form-control form-control-sm"
                value={draft}
                maxLength={200}
                autoFocus
                placeholder="e.g. Study Medication"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    save();
                  }
                  if (e.key === "Escape") setOpen(false);
                }}
              />
              <div className="epsf-setup-label-edit__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={save}
                  disabled={busy}
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function parseKitList(text) {
  return [
    ...new Set(
      String(text || "")
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

function dateFromInputValue(raw) {
  const s = toDateInputValue(raw);
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function newPrepareRowId() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toDateInputValue(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function batchKeyOf(batchNumber, expiryDate) {
  const bn = String(batchNumber || "").trim();
  if (!bn) return "";
  return `${bn}|||${toDateInputValue(expiryDate)}`;
}

/** Days ahead that count as near expiry for batch chips. */
const NEAR_EXPIRY_DAYS = 30;

/** @returns {'expired'|'near'|'ok'|'none'} */
function expiryToneOf(expiryDate) {
  const iso = toDateInputValue(expiryDate);
  if (!iso) return "none";
  const exp = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(exp.getTime())) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return "expired";
  if (diffDays <= NEAR_EXPIRY_DAYS) return "near";
  return "ok";
}

/** @returns {'mapped'|'partial'|'none'} */
function mappingToneOf(mappedCount, kitCount) {
  const mapped = Number(mappedCount) || 0;
  const total = Number(kitCount) || 0;
  if (total <= 0 || mapped <= 0) return "none";
  if (mapped >= total) return "mapped";
  return "partial";
}

function mappingLabelOf(mappedCount, kitCount) {
  const tone = mappingToneOf(mappedCount, kitCount);
  if (tone === "mapped") return "Mapped";
  if (tone === "partial") return "Partial";
  return "Not mapped";
}

function expiryChipLabel(expiryDate) {
  const iso = toDateInputValue(expiryDate);
  if (!iso) return "No expiry";
  const tone = expiryToneOf(iso);
  if (tone === "expired") return `Expired ${iso}`;
  return `Exp ${iso}`;
}

function buildPendingBatchOptions(kitMaps) {
  const byKey = new Map();
  (kitMaps || []).forEach((row) => {
    const batchNumber = String(
      row?.BatchNumber || row?.batchNumber || "",
    ).trim();
    const expiryDate = toDateInputValue(
      row?.ExpiryDate || row?.expiryDate || "",
    );
    const key = batchNumber
      ? batchKeyOf(batchNumber, expiryDate)
      : `___none___|||${expiryDate}`;
    const needs =
      String(row?.MappingStatus || row?.mappingStatus || "") ===
      "RequiresSponsorResolution";
    if (byKey.has(key)) {
      const cur = byKey.get(key);
      cur.kitCount += 1;
      if (needs) cur.pendingCount += 1;
      else cur.mappedCount += 1;
      return;
    }
    byKey.set(key, {
      value: key,
      batchNumber,
      expiryDate,
      kitCount: 1,
      pendingCount: needs ? 1 : 0,
      mappedCount: needs ? 0 : 1,
    });
  });
  return [...byKey.values()]
    .map((opt) => {
      const mappingLabel = mappingLabelOf(opt.mappedCount, opt.kitCount);
      return {
        ...opt,
        mappingLabel,
        expiryTone: expiryToneOf(opt.expiryDate),
        mappingTone: mappingToneOf(opt.mappedCount, opt.kitCount),
        label: `${opt.batchNumber || "(No batch / lot)"}${
          opt.expiryDate ? ` · exp ${opt.expiryDate}` : ""
        } · ${opt.kitCount} kit${opt.kitCount === 1 ? "" : "s"}`,
      };
    })
    .sort((a, b) => {
      if (a.pendingCount > 0 && b.pendingCount === 0) return -1;
      if (a.pendingCount === 0 && b.pendingCount > 0) return 1;
      return a.label.localeCompare(b.label);
    });
}

function tabFromPath(pathname) {
  const path = String(pathname || "").toLowerCase();
  if (path.includes("/kit-mapping")) return "kits";
  return "products";
}

function impIdOf(row) {
  return String(row?.ImpProductId || row?.impProductId || "").trim();
}

function studyImpIdOf(row) {
  return String(row?.StudyImpProductId || row?.studyImpProductId || "").trim();
}

function productCodeOf(row) {
  return String(row?.ProductCode || row?.productCode || "").trim();
}

function productNameOf(row) {
  return String(row?.ProductName || row?.productName || "").trim();
}

/**
 * Restricted Sponsor / delegated setup:
 * Study IMP products → Treatment (ACTIVE/PLACEBO/COMPARATOR) → Kit mapping.
 * Blinded site users must not reach this screen.
 */
export default function ImpSponsorSetup() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    moduleAccess,
    canTreatmentSetup,
    canKitTreatmentManage,
  } = useEpsfAccess();
  const allow = moduleAccess && (canTreatmentSetup || canKitTreatmentManage);
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [tab, setTab] = useState(() => tabFromPath(location.pathname));
  const [catalogue, setCatalogue] = useState([]);
  const [studyProducts, setStudyProducts] = useState([]);
  const [kitMaps, setKitMaps] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingImpId, setSavingImpId] = useState("");
  const [labelDrafts, setLabelDrafts] = useState({});
  const [treatmentFilters, setTreatmentFilters] = useState([]);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLabelDraft, setDetailLabelDraft] = useState("");
  const successClearRef = useRef(null);

  const [prepareModalOpen, setPrepareModalOpen] = useState(false);
  const [pendingBatchKey, setPendingBatchKey] = useState("");
  const [kitImpByNumber, setKitImpByNumber] = useState({});
  const [prepareForm, setPrepareForm] = useState({
    batchNumber: "",
    expiryDate: "",
    shipmentReference: "",
    temperature: "",
    rows: [],
  });
  const [prepareDraftKit, setPrepareDraftKit] = useState("");
  const [prepareDraftExpiry, setPrepareDraftExpiry] = useState(null);
  const [prepareHint, setPrepareHint] = useState("");
  const [preparePasteBulk, setPreparePasteBulk] = useState("");
  const [batchListQuery, setBatchListQuery] = useState("");
  const [phoneScan, setPhoneScan] = useState(null);
  const [phoneScanBusy, setPhoneScanBusy] = useState(false);
  const [phoneScanHint, setPhoneScanHint] = useState("");
  const phonePollRef = useRef(null);

  const stopPreparePhoneScan = useCallback(async () => {
    if (phonePollRef.current) {
      window.clearInterval(phonePollRef.current);
      phonePollRef.current = null;
    }
    const id = phoneScan?.sessionId;
    setPhoneScan(null);
    setPhoneScanHint("");
    if (id) {
      try {
        await closeReceiveScanSession(id);
      } catch {
        /* ignore */
      }
    }
  }, [phoneScan?.sessionId]);

  const openPrepareModal = () => {
    setPrepareForm({
      batchNumber: "",
      expiryDate: "",
      shipmentReference: "",
      temperature: "",
      rows: [],
    });
    setPrepareDraftKit("");
    setPrepareDraftExpiry(null);
    setPrepareHint("");
    setPreparePasteBulk("");
    setPhoneScan(null);
    setPhoneScanHint("");
    setPrepareModalOpen(true);
  };

  const closePrepareModal = () => {
    if (busy) return;
    const hasDraft =
      Boolean(String(prepareForm.batchNumber || "").trim()) ||
      Boolean(String(prepareDraftKit || "").trim()) ||
      Boolean(String(preparePasteBulk || "").trim()) ||
      (Array.isArray(prepareForm.rows) && prepareForm.rows.length > 0);
    const finishClose = () => {
      stopPreparePhoneScan();
      setPrepareModalOpen(false);
    };
    if (hasDraft) {
      showAlert({
        title: "Discard batch details?",
        message:
          "Cancel discards typed kit numbers and batch details without saving. Do you want to continue?",
        type: "warning",
        buttonText: "Discard",
        cancelText: "Keep editing",
        onConfirm: () => {
          hideAlert();
          finishClose();
        },
      });
      return;
    }
    finishClose();
  };

  useEffect(() => {
    const next = tabFromPath(location.pathname);
    setTab((prev) => (prev === next ? prev : next));
  }, [location.pathname]);

  useEffect(() => {
    if (tab === "kits" && !canKitTreatmentManage && canTreatmentSetup) {
      setTab("products");
      if (String(location.pathname || "").includes("/kit-mapping")) {
        navigate("/epsf/imp/sponsor-setup", { replace: true });
      }
    } else if (tab === "products" && !canTreatmentSetup && canKitTreatmentManage) {
      setTab("kits");
      if (!String(location.pathname || "").includes("/kit-mapping")) {
        navigate("/epsf/imp/kit-mapping", { replace: true });
      }
    }
  }, [
    tab,
    canKitTreatmentManage,
    canTreatmentSetup,
    location.pathname,
    navigate,
  ]);

  const crumbLabel = tab === "kits" ? "Kit mapping" : "Treatment setup";

  const treatmentSummary = useMemo(() => {
    const rows = studyProducts || [];
    let active = 0;
    let placebo = 0;
    let comparator = 0;
    for (const r of rows) {
      const t = String(r.TreatmentType || r.treatmentType || "")
        .trim()
        .toUpperCase();
      if (t === "ACTIVE") active += 1;
      else if (t === "PLACEBO") placebo += 1;
      else if (t === "COMPARATOR") comparator += 1;
    }
    return { total: rows.length, active, placebo, comparator };
  }, [studyProducts]);

  const assignmentByImpId = useMemo(() => {
    const map = new Map();
    (studyProducts || []).forEach((row) => {
      const id = impIdOf(row);
      if (id) map.set(id, row);
    });
    return map;
  }, [studyProducts]);

  useEffect(
    () => () => {
      if (successClearRef.current) {
        window.clearTimeout(successClearRef.current);
      }
    },
    [],
  );

  const flashSuccess = useCallback((text) => {
    setSuccess(text);
    if (successClearRef.current) {
      window.clearTimeout(successClearRef.current);
    }
    successClearRef.current = window.setTimeout(() => {
      setSuccess("");
      successClearRef.current = null;
    }, 3500);
  }, []);

  const loadAll = useCallback(async () => {
    if (!ctx.studyId) {
      setCatalogue([]);
      setStudyProducts([]);
      setKitMaps([]);
      setError("");
      setSuccess("");
      return { kitMaps: [], studyProducts: [] };
    }
    setError("");
    try {
      const studyRes = await fetchStudyImpProducts(
        ctx.studyId,
        true,
        ctx.siteId || null,
      );
      if (!studyRes.success) {
        throw new Error(studyRes.message || "Failed to load study products.");
      }
      setStudyProducts(studyRes.rows || []);

      if (canTreatmentSetup) {
        const prodRes = await fetchImpProducts(null, null, true);
        if (prodRes.success) {
          setCatalogue(prodRes.rows || []);
        } else {
          setCatalogue([]);
        }
      } else {
        setCatalogue([]);
      }

      let mapRows = [];
      if (canKitTreatmentManage) {
        const mapRes = await fetchStudyImpKitMaps(
          ctx.studyId,
          ctx.siteId || null,
        );
        if (!mapRes.success) {
          throw new Error(mapRes.message || "Failed to load kit maps.");
        }
        mapRows = mapRes.rows || [];
        setKitMaps(mapRows);
      } else {
        setKitMaps([]);
      }
      return { kitMaps: mapRows, studyProducts: studyRes.rows || [] };
    } catch (e) {
      setError(e.message || "Failed to load restricted setup.");
      return { kitMaps: [], studyProducts: [] };
    }
  }, [
    ctx.studyId,
    ctx.siteId,
    canKitTreatmentManage,
    canTreatmentSetup,
  ]);

  useEffect(() => {
    if (!allow || !ctx.studyId) return undefined;
    let cancelled = false;
    (async () => {
      await loadAll();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [allow, ctx.studyId, ctx.siteId, loadAll]);

  useEffect(() => {
    const next = {};
    (catalogue || []).forEach((row) => {
      const id = impIdOf(row);
      if (!id) return;
      const assigned = assignmentByImpId.get(id);
      next[id] = assigned
        ? String(
            assigned.NeutralDisplayName ||
              assigned.neutralDisplayName ||
              "",
          )
        : productNameOf(row);
    });
    setLabelDrafts(next);
  }, [catalogue, assignmentByImpId]);

  const treatmentTableRows = useMemo(() => {
    // Keep catalogue order — do not re-sort by treatment status after updates.
    return (catalogue || [])
      .filter((row) => Boolean(impIdOf(row)))
      .map((row) => {
        const id = impIdOf(row);
        const assigned = assignmentByImpId.get(id);
        const treatment =
          assigned?.TreatmentType || assigned?.treatmentType || "";
        return {
          ...row,
          ImpProductId: id,
          ProductCode: productCodeOf(row),
          ProductName: productNameOf(row),
          Product: `${productNameOf(row)} ${productCodeOf(row)}`.trim(),
          DosageForm: String(row.DosageForm || row.dosageForm || "").trim(),
          Route: String(row.Route || row.route || "").trim(),
          Manufacturer: String(
            row.Manufacturer || row.manufacturer || "",
          ).trim(),
          DosageStrength: formatDosageStrengthDisplay(row),
          StorageCondition:
            formatStorageTempRange(
              row.StorageTempMin ?? row.storageTempMin,
              row.StorageTempMax ?? row.storageTempMax,
              row.StorageTempUnit || row.storageTempUnit,
              row.StorageCondition || row.storageCondition || "",
            ) || "",
          TreatmentType: treatment || "Unassigned",
          NeutralDisplayName:
            assigned?.NeutralDisplayName ||
            assigned?.neutralDisplayName ||
            productNameOf(row),
        };
      });
  }, [catalogue, assignmentByImpId]);

  const treatmentFieldOptions = useMemo(
    () => [
      { key: "Product", label: "IMP" },
      { key: "TreatmentType", label: "Treatment" },
      { key: "NeutralDisplayName", label: "Site-facing label" },
      { key: "StorageCondition", label: "Temperature" },
      { key: "DosageStrength", label: "Strength" },
      { key: "Route", label: "Route" },
      { key: "Manufacturer", label: "Manufacturer" },
    ],
    [],
  );
  const treatmentDefaultColumns = useMemo(
    () => [
      "Product",
      "TreatmentType",
      "NeutralDisplayName",
      "StorageCondition",
      "DosageStrength",
      "Route",
    ],
    [],
  );

  const filteredTreatmentRows = useMemo(() => {
    if (!treatmentFilters.length) return treatmentTableRows;
    const selected = new Set(
      treatmentFilters.map((v) => String(v || "").trim().toUpperCase()),
    );
    const wantAssigned = selected.has("ASSIGNED");
    const wantUnassigned = selected.has("UNASSIGNED");
    return treatmentTableRows.filter((row) => {
      const raw = String(row.TreatmentType || "Unassigned").trim();
      const key = raw.toUpperCase();
      const isUnassigned = !key || key === "UNASSIGNED" || key === "—";
      if (wantAssigned && !isUnassigned) return true;
      if (wantUnassigned && isUnassigned) return true;
      return selected.has(key);
    });
  }, [treatmentTableRows, treatmentFilters]);

  const assignedCount = useMemo(
    () =>
      (catalogue || []).filter((row) =>
        assignmentByImpId.has(impIdOf(row)),
      ).length,
    [catalogue, assignmentByImpId],
  );
  const unassignedCount = useMemo(
    () =>
      (catalogue || []).filter(
        (row) => !assignmentByImpId.has(impIdOf(row)),
      ).length,
    [catalogue, assignmentByImpId],
  );

  const kpiFilterKey = useMemo(() => {
    if (treatmentFilters.length !== 1) return "";
    return String(treatmentFilters[0] || "").trim().toUpperCase();
  }, [treatmentFilters]);

  const toggleKpiFilter = useCallback((key) => {
    const next = String(key || "").trim();
    if (!next) {
      setTreatmentFilters([]);
      return;
    }
    setTreatmentFilters((prev) => {
      if (
        prev.length === 1 &&
        String(prev[0] || "").trim().toUpperCase() === next.toUpperCase()
      ) {
        return [];
      }
      return [next];
    });
  }, []);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (field === "TreatmentType") return true;
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (field === "Product") {
      const hay = `${productCodeOf(row)} ${productNameOf(row)}`.toLowerCase();
      return hay.includes(term);
    }
    if (field === "DosageStrength") {
      return formatDosageStrengthDisplay(row).toLowerCase().includes(term);
    }
    if (field === "StorageCondition") {
      const label =
        formatStorageTempRange(
          row.StorageTempMin ?? row.storageTempMin,
          row.StorageTempMax ?? row.storageTempMax,
          row.StorageTempUnit || row.storageTempUnit,
          row.StorageCondition || row.storageCondition || "",
        ) || "";
      return label.toLowerCase().includes(term);
    }
    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback((field, options) => {
    if (field === "TreatmentType") {
      if (options?.checkOnly) return true;
      return (
        <BadgeMultiSelect
          options={TREATMENT_FILTER_OPTIONS}
          value={treatmentFilters}
          onChange={setTreatmentFilters}
          emptyLabel="All"
          addLabel="+"
          popoverTitle="Filter treatment"
          popoverSubtitle="Select one or more treatments"
          showSelectAll
          compactSummary
          disabled={options?.disabled}
        />
      );
    }
    return null;
  }, [treatmentFilters]);

  const openProductDetail = useCallback(
    (row) => {
      const id = impIdOf(row);
      const assigned = assignmentByImpId.get(id);
      setDetailRow(row);
      setDetailLabelDraft(
        String(
          labelDrafts[id] ??
            assigned?.NeutralDisplayName ??
            assigned?.neutralDisplayName ??
            productNameOf(row) ??
            "",
        ),
      );
    },
    [assignmentByImpId, labelDrafts],
  );

  const closeProductDetail = () => {
    setDetailRow(null);
    setDetailLabelDraft("");
  };

  const studyProductOptions = useMemo(
    () =>
      (studyProducts || []).map((p) => {
        const code = productCodeOf(p) || "—";
        const name = productNameOf(p) || "IMP";
        return {
          value: studyImpIdOf(p),
          code,
          name,
          label: `${code} · ${name}`,
          impProductId: impIdOf(p),
          row: p,
        };
      }),
    [studyProducts],
  );

  const pendingBatchOptions = useMemo(
    () => buildPendingBatchOptions(kitMaps),
    [kitMaps],
  );

  const pendingKitsForBatch = useMemo(() => {
    if (!pendingBatchKey) return [];
    return (kitMaps || []).filter((row) => {
      const batchNumber = String(
        row?.BatchNumber || row?.batchNumber || "",
      ).trim();
      const expiryDate = toDateInputValue(
        row?.ExpiryDate || row?.expiryDate || "",
      );
      const key = batchNumber
        ? batchKeyOf(batchNumber, expiryDate)
        : `___none___|||${expiryDate}`;
      return key === pendingBatchKey;
    });
  }, [kitMaps, pendingBatchKey]);

  const selectedPendingBatch = useMemo(
    () =>
      pendingBatchOptions.find((o) => o.value === pendingBatchKey) || null,
    [pendingBatchOptions, pendingBatchKey],
  );

  const kitMappingDirty = useMemo(() => {
    if (!pendingKitsForBatch.length) return false;
    return pendingKitsForBatch.some((row) => {
      const kit = String(row.KitNumber || row.kitNumber || "").trim();
      if (!kit) return false;
      const saved = String(
        row.StudyImpProductId || row.studyImpProductId || "",
      )
        .trim()
        .toLowerCase();
      const current = String(kitImpByNumber[kit] || "")
        .trim()
        .toLowerCase();
      if (!current) return false;
      return current !== saved;
    });
  }, [pendingKitsForBatch, kitImpByNumber]);

  const canSavePendingMapping =
    Boolean(ctx.studyId) &&
    pendingKitsForBatch.length > 0 &&
    kitMappingDirty;

  const filteredBatchOptions = useMemo(() => {
    const q = String(batchListQuery || "")
      .trim()
      .toLowerCase();
    if (!q) return pendingBatchOptions;
    return pendingBatchOptions.filter((opt) => {
      const hay = `${opt.batchNumber || ""} ${opt.expiryDate || ""} ${
        opt.mappingLabel || ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [pendingBatchOptions, batchListQuery]);

  useEffect(() => {
    if (!pendingBatchKey) {
      setKitImpByNumber({});
      return;
    }
    setError("");
    const next = {};
    pendingKitsForBatch.forEach((row) => {
      const kit = String(row.KitNumber || row.kitNumber || "").trim();
      if (!kit) return;
      const sip = String(
        row.StudyImpProductId || row.studyImpProductId || "",
      ).trim();
      if (sip) next[kit] = sip;
    });
    setKitImpByNumber(next);
  }, [pendingBatchKey, pendingKitsForBatch]);

  useEffect(() => {
    if (!pendingBatchKey && pendingBatchOptions.length === 1) {
      setPendingBatchKey(pendingBatchOptions[0].value);
    } else if (
      pendingBatchKey &&
      pendingBatchOptions.length &&
      !pendingBatchOptions.some((o) => o.value === pendingBatchKey)
    ) {
      setPendingBatchKey("");
    }
  }, [pendingBatchOptions, pendingBatchKey]);

  const kitFieldOptions = useMemo(
    () => [
      { key: "KitNumber", label: "Kit" },
      { key: "DisplayStatus", label: "Mapping" },
      { key: "InventoryStatus", label: "Inventory" },
      { key: "BatchNumber", label: "Batch / Lot" },
      { key: "ExpiryDate", label: "Expiry" },
      { key: "TreatmentType", label: "Treatment" },
      { key: "ProductCode", label: "Code" },
      { key: "ProductName", label: "Product" },
    ],
    [],
  );

  const pendingKitFieldOptions = useMemo(
    () => [
      { key: "KitNumber", label: "Kit" },
      { key: "AssignImp", label: "Assign IMP" },
      { key: "ExpiryDate", label: "Expiry" },
      { key: "DisplayStatus", label: "IMP mapped" },
      { key: "InventoryStatus", label: "Inventory" },
    ],
    [],
  );

  const pendingKitDefaultColumns = useMemo(
    () => pendingKitFieldOptions.map((c) => c.key),
    [pendingKitFieldOptions],
  );
  const kitDefaultColumns = useMemo(
    () => kitFieldOptions.map((c) => c.key),
    [kitFieldOptions],
  );

  const PAGE_SIZE_OPTIONS = useMemo(() => [10, 25, 50], []);

  const saveKitMappings = async ({
    studyImpProductId,
    kits,
    batchNumber,
    expiryDate,
    kitLines,
    successMessage,
    skipReload = false,
  }) => {
    const batch = String(batchNumber || "").trim() || null;
    const expiry = String(expiryDate || "").trim() || null;
    const kitList = (kits || []).map((k) => String(k || "").trim()).filter(Boolean);
    if (!ctx.studyId || !studyImpProductId || !kitList.length) {
      setError("Select a study IMP / treatment and at least one kit.");
      return false;
    }
    const lines =
      Array.isArray(kitLines) && kitLines.length
        ? kitLines
        : kitList.map((kit) => ({
            KitNumber: kit,
            BatchNumber: batch,
            ExpiryDate: expiry,
          }));
    const result = await saveStudyImpKitMaps({
      StudyId: ctx.studyId,
      SiteId: ctx.siteId || null,
      StudyImpProductId: studyImpProductId,
      KitNumbers: JSON.stringify(kitList),
      KitLinesJson: JSON.stringify(lines),
      BatchNumber: batch,
      ExpiryDate: expiry,
    });
    if (!result.success) {
      throw new Error(result.message || "Kit mapping failed.");
    }
    if (!skipReload) {
      await loadAll();
      flashSuccess(result.message || successMessage || "Kit mapping saved.");
    }
    return true;
  };

  const onMapPendingBatch = async (e) => {
    e?.preventDefault?.();
    if (!pendingBatchKey || !selectedPendingBatch) {
      setError("Select a batch on the left first.");
      return;
    }
    if (!pendingKitsForBatch.length) {
      setError("No pending kits in this batch.");
      return;
    }
    if (!kitMappingDirty) {
      setError("");
      return;
    }

    const savedKey = pendingBatchKey;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const groups = new Map();
      for (const row of pendingKitsForBatch) {
        const kit = String(row.KitNumber || row.kitNumber || "").trim();
        const sip = kitImpByNumber[kit] || "";
        const savedSip = String(
          row.StudyImpProductId || row.studyImpProductId || "",
        ).trim();
        if (!kit) continue;
        if (!sip) {
          const needs =
            String(row?.MappingStatus || row?.mappingStatus || "") ===
            "RequiresSponsorResolution";
          if (!needs && savedSip) continue;
          showAlert({
            title: "Assign IMP",
            message: "Assign a study IMP to every kit that needs mapping.",
            type: "info",
            buttonText: "OK",
          });
          return;
        }
        // Only send kits whose assignment actually changed (or was unmapped).
        if (
          savedSip &&
          savedSip.toLowerCase() === String(sip).trim().toLowerCase()
        ) {
          continue;
        }
        if (!groups.has(sip)) groups.set(sip, []);
        groups.get(sip).push(kit);
      }
      if (!groups.size) {
        flashSuccess("No mapping changes to save.");
        return;
      }
      for (const [sip, kits] of groups.entries()) {
        await saveKitMappings({
          studyImpProductId: sip,
          kits,
          batchNumber: selectedPendingBatch.batchNumber,
          expiryDate: selectedPendingBatch.expiryDate,
          skipReload: true,
        });
      }
      const loaded = await loadAll();
      flashSuccess("Kit mapping saved.");
      const options = buildPendingBatchOptions(loaded?.kitMaps || []);
      const incomplete = options.filter((o) => o.pendingCount > 0);
      const next =
        incomplete.find((o) => o.value !== savedKey) || incomplete[0] || null;
      setPendingBatchKey(next?.value || "");
      if (!next) setKitImpByNumber({});
    } catch (err) {
      setError(err.message || "Kit mapping failed.");
    } finally {
      setBusy(false);
    }
  };

  const addPrepareKitRow = () => {
    const kit = String(prepareDraftKit || "").trim();
    if (!kit) {
      setPrepareHint("Enter a kit number.");
      return;
    }
    if (kit.length > 100) {
      setPrepareHint("Kit number cannot exceed 100 characters.");
      return;
    }
    const exists = (prepareForm.rows || []).some(
      (r) =>
        String(r.kitNumber || "")
          .trim()
          .toUpperCase() === kit.toUpperCase(),
    );
    if (exists) {
      setPrepareHint(`Kit ${kit} has already been added.`);
      return;
    }
    const expiry =
      toDateInputValue(prepareDraftExpiry) ||
      toDateInputValue(prepareForm.expiryDate) ||
      "";
    setPrepareForm((f) => ({
      ...f,
      rows: [
        ...(f.rows || []),
        {
          id: newPrepareRowId(),
          kitNumber: kit,
          expiryDate: expiry,
          studyImpProductId: "",
        },
      ],
    }));
    setPrepareDraftKit("");
    setPrepareDraftExpiry(
      prepareForm.expiryDate ? dateFromInputValue(prepareForm.expiryDate) : null,
    );
    setPrepareHint("");
  };

  const addPrepareKitsFromList = useCallback(
    (incoming, { expiryOverride, batchFromScan } = {}) => {
      const list = Array.isArray(incoming) ? incoming : [];
      let addedCount = 0;
      let firstDupe = "";
      setPrepareForm((f) => {
        const existing = new Set(
          (f.rows || []).map((r) =>
            String(r.kitNumber || "")
              .trim()
              .toUpperCase(),
          ),
        );
        const fresh = [];
        list.forEach((raw) => {
          const kit = String(raw || "").trim();
          if (!kit) return;
          const key = kit.toUpperCase();
          if (existing.has(key) || fresh.some((x) => x.toUpperCase() === key)) {
            if (!firstDupe) firstDupe = kit;
            return;
          }
          existing.add(key);
          fresh.push(kit);
        });
        addedCount = fresh.length;
        if (!fresh.length) return f;
        const expiry =
          toDateInputValue(expiryOverride) ||
          toDateInputValue(prepareDraftExpiry) ||
          toDateInputValue(f.expiryDate) ||
          "";
        const nextBatch =
          !String(f.batchNumber || "").trim() && batchFromScan
            ? String(batchFromScan).trim()
            : f.batchNumber;
        return {
          ...f,
          batchNumber: nextBatch,
          rows: [
            ...(f.rows || []),
            ...fresh.map((kitNumber) => ({
              id: newPrepareRowId(),
              kitNumber,
              expiryDate: expiry,
              studyImpProductId: "",
            })),
          ],
        };
      });
      if (!addedCount) {
        setPrepareHint(
          firstDupe
            ? `Kit ${firstDupe} has already been added.`
            : "No kit numbers found to add.",
        );
        return 0;
      }
      setPrepareHint(
        firstDupe ? `Added ${addedCount}. Skipped duplicate ${firstDupe}.` : "",
      );
      return addedCount;
    },
    [prepareDraftExpiry],
  );

  const applyPreparePasteBulk = () => {
    const kits = parseKitList(preparePasteBulk);
    if (!kits.length) {
      setPrepareHint("Paste at least one kit number.");
      return;
    }
    const added = addPrepareKitsFromList(kits);
    if (added > 0) setPreparePasteBulk("");
  };

  const applyPreparePhoneScanEvents = useCallback(
    (events) => {
      if (!Array.isArray(events) || !events.length) return;
      const kits = [];
      let batchFromScan = "";
      let expiryFromScan = "";
      events.forEach((ev) => {
        const kit = String(ev.kitNumber || "").trim();
        if (kit) kits.push(kit);
        if (!batchFromScan && ev.batchNumber) {
          batchFromScan = String(ev.batchNumber).trim();
        }
        if (!expiryFromScan && ev.expiryDate) {
          expiryFromScan = toDateInputValue(ev.expiryDate);
        }
      });
      const added = addPrepareKitsFromList(kits, {
        expiryOverride: expiryFromScan,
        batchFromScan,
      });
      if (added > 0) {
        setPhoneScanHint(`Added ${added} kit(s) from phone`);
      }
    },
    [addPrepareKitsFromList],
  );

  const startPreparePhoneScan = useCallback(async () => {
    if (!ctx.studyId || phoneScanBusy) return;
    const scanSiteId =
      String(ctx.siteId || "").trim() ||
      String(ctx.siteOptions?.[0]?.value || "").trim();
    if (!scanSiteId) {
      showAlert({
        title: "Select site",
        message: "Select a site in the breadcrumb bar to use phone scan.",
        type: "info",
        buttonText: "OK",
      });
      return;
    }
    if (!String(ctx.siteId || "").trim() && typeof ctx.setSiteId === "function") {
      ctx.setSiteId(scanSiteId);
    }
    setPhoneScanBusy(true);
    setPhoneScanHint("");
    try {
      if (phoneScan?.sessionId) {
        await closeReceiveScanSession(phoneScan.sessionId).catch(() => {});
      }
      const res = await createReceiveScanSession(ctx.studyId, scanSiteId);
      if (!res.success || !res.session?.accessToken) {
        setPhoneScanHint(res.message || "Could not start phone scan session.");
        return;
      }
      const code = res.session.shortCode || res.session.accessToken;
      const siteOrigin = String(window.location.origin || "").replace(/\/$/, "");
      const phoneUrl = `${siteOrigin}/s/${encodeURIComponent(String(code).trim().toUpperCase())}`;
      setPhoneScan({
        sessionId: res.session.scanSessionId,
        token: code,
        shortCode: code,
        url: phoneUrl,
      });
      setPhoneScanHint("Scan this QR with your phone to add kits.");
      if (phonePollRef.current) window.clearInterval(phonePollRef.current);
      phonePollRef.current = window.setInterval(async () => {
        try {
          const poll = await pollReceiveScanEvents(res.session.scanSessionId);
          if (!poll.success) return;
          if (poll.events?.length) applyPreparePhoneScanEvents(poll.events);
          if (poll.session && !poll.session.isActive) {
            window.clearInterval(phonePollRef.current);
            phonePollRef.current = null;
            setPhoneScanHint("Phone scan session ended.");
            setPhoneScan(null);
          }
        } catch {
          /* keep polling */
        }
      }, 1500);
    } catch (e) {
      setPhoneScanHint(e?.message || "Could not start phone scan session.");
    } finally {
      setPhoneScanBusy(false);
    }
  }, [
    ctx.studyId,
    ctx.siteId,
    ctx.siteOptions,
    ctx.setSiteId,
    phoneScanBusy,
    phoneScan?.sessionId,
    applyPreparePhoneScanEvents,
    showAlert,
  ]);

  useEffect(
    () => () => {
      if (phonePollRef.current) {
        window.clearInterval(phonePollRef.current);
        phonePollRef.current = null;
      }
    },
    [],
  );

  const onPrepareBatch = async (e) => {
    e.preventDefault();
    const batch = String(prepareForm.batchNumber || "").trim();
    const rows = (prepareForm.rows || [])
      .map((r) => ({
        kitNumber: String(r.kitNumber || "").trim(),
        expiryDate:
          toDateInputValue(r.expiryDate) ||
          toDateInputValue(prepareForm.expiryDate) ||
          "",
        studyImpProductId: String(r.studyImpProductId || "").trim(),
      }))
      .filter((r) => r.kitNumber);

    if (!batch) {
      setPrepareHint("Enter a batch / lot number.");
      return;
    }
    if (!toDateInputValue(prepareForm.expiryDate)) {
      setPrepareHint("Enter batch expiry.");
      return;
    }
    if (!rows.length) {
      setPrepareHint("Add at least one kit.");
      return;
    }
    if (rows.some((r) => !r.studyImpProductId)) {
      setPrepareHint("Assign a study IMP to every kit.");
      showAlert({
        title: "Assign IMP",
        message: "Assign a study IMP to every kit.",
        type: "info",
        buttonText: "OK",
      });
      return;
    }
    if (rows.some((r) => !r.expiryDate)) {
      setPrepareHint("Set expiry on the batch or on each kit row.");
      return;
    }
    const tempText = String(prepareForm.temperature || "").trim();
    if (tempText && Number.isNaN(Number(tempText))) {
      showAlert({
        title: "Temperature",
        message: "Temperature must be a valid number.",
        type: "info",
        buttonText: "OK",
      });
      return;
    }
    if (String(prepareForm.shipmentReference || "").trim().length > 100) {
      showAlert({
        title: "Shipment / reference",
        message: "Shipment / reference cannot exceed 100 characters.",
        type: "info",
        buttonText: "OK",
      });
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const groups = new Map();
      for (const row of rows) {
        if (!groups.has(row.studyImpProductId)) {
          groups.set(row.studyImpProductId, []);
        }
        groups.get(row.studyImpProductId).push(row);
      }
      for (const [sip, groupRows] of groups.entries()) {
        await saveKitMappings({
          studyImpProductId: sip,
          kits: groupRows.map((r) => r.kitNumber),
          batchNumber: batch,
          expiryDate: prepareForm.expiryDate || groupRows[0]?.expiryDate || "",
          kitLines: groupRows.map((r) => ({
            KitNumber: r.kitNumber,
            BatchNumber: batch,
            ExpiryDate: r.expiryDate || null,
          })),
          skipReload: true,
        });
      }
      await loadAll();
      flashSuccess("Batch prepared and kits mapped.");
      setPrepareForm({
        batchNumber: "",
        expiryDate: "",
        shipmentReference: "",
        temperature: "",
        rows: [],
      });
      setPrepareDraftKit("");
      setPrepareDraftExpiry(null);
      setPrepareHint("");
      setPreparePasteBulk("");
      stopPreparePhoneScan();
      setPrepareModalOpen(false);
    } catch (err) {
      setError(err.message || "Kit mapping failed.");
    } finally {
      setBusy(false);
    }
  };

  const saveStudyTreatment = async ({
    impProductId,
    treatmentType,
    neutralDisplayName,
    successMessage = "Saved.",
  }) => {
    if (!ctx.studyId || !impProductId || !treatmentType) {
      setError("Select a study and treatment.");
      return false;
    }
    setSavingImpId(impProductId);
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await saveStudyImpProduct({
        StudyId: ctx.studyId,
        ImpProductId: impProductId,
        TreatmentType: treatmentType,
        NeutralDisplayName: neutralDisplayName || null,
        IsActive: true,
        SiteIdsJson: ctx.siteId ? JSON.stringify([ctx.siteId]) : null,
      });
      if (!result.success) throw new Error(result.message || "Save failed.");
      await loadAll();
      flashSuccess(result.message || successMessage);
      return true;
    } catch (err) {
      setError(err.message || "Save failed.");
      return false;
    } finally {
      setSavingImpId("");
      setBusy(false);
    }
  };

  const onClassify = async (row, treatmentType) => {
    const id = impIdOf(row);
    const existing = assignmentByImpId.get(id);
    const current = String(
      existing?.TreatmentType || existing?.treatmentType || "",
    )
      .trim()
      .toUpperCase();
    if (current === treatmentType) return;
    await saveStudyTreatment({
      impProductId: id,
      treatmentType,
      neutralDisplayName:
        String(labelDrafts[id] ?? "").trim() ||
        existing?.NeutralDisplayName ||
        existing?.neutralDisplayName ||
        productNameOf(row) ||
        "",
      successMessage: "Treatment updated.",
    });
  };

  const onSaveLabel = async (row, nextLabel) => {
    const impId = impIdOf(row);
    const assigned = assignmentByImpId.get(impId);
    if (!assigned) return;
    const draft = String(nextLabel ?? labelDrafts[impId] ?? "").trim();
    const current = String(
      assigned.NeutralDisplayName || assigned.neutralDisplayName || "",
    ).trim();
    if (draft === current) return;
    setLabelDrafts((prev) => ({ ...prev, [impId]: draft }));
    await saveStudyTreatment({
      impProductId: impId,
      treatmentType: String(
        assigned.TreatmentType || assigned.treatmentType || "ACTIVE",
      ),
      neutralDisplayName: draft,
      successMessage: "Site-facing label saved.",
    });
  };

  const renderTreatmentSetupCell = useCallback(
    (row, field) => {
      const id = impIdOf(row);
      const assigned = assignmentByImpId.get(id);
      const current =
        assigned?.TreatmentType || assigned?.treatmentType || "";
      const rowBusy = busy && savingImpId === id;

      if (field === "Product") {
        return renderImpProductCell(row, {
          as: "button",
          type: "button",
          className: "report-table__cell-link",
          title: "View IMP details",
          onClick: () => openProductDetail(row),
        });
      }

      if (field === "DosageStrength") {
        const { dosage, strength } = getDosageAndStrength(row);
        if (!dosage && !strength) return "—";
        return (
          <div
            className="epsf-imp-product-cell"
            title={[dosage, strength].filter(Boolean).join(" ")}
          >
            <span className="epsf-imp-product-cell__name">
              {dosage || "—"}
            </span>
            {strength ? (
              <span className="epsf-imp-product-cell__code">{strength}</span>
            ) : null}
          </div>
        );
      }

      if (field === "Route") {
        return row.Route || row.route || "—";
      }

      if (field === "Manufacturer") {
        return row.Manufacturer || row.manufacturer || "—";
      }

      if (field === "StorageCondition") {
        const label =
          formatStorageTempRange(
            row.StorageTempMin ?? row.storageTempMin,
            row.StorageTempMax ?? row.storageTempMax,
            row.StorageTempUnit || row.storageTempUnit,
            row.StorageCondition || row.storageCondition || "",
          ) || "";
        if (!label) return "—";
        const tone = getStorageTempBadgeTone(
          row.StorageTempMin ?? row.storageTempMin,
          row.StorageTempMax ?? row.storageTempMax,
          row.StorageTempUnit || row.storageTempUnit,
        );
        return (
          <span
            className={`epsf-imp-pill epsf-imp-pill--temp-${tone}`}
            title={label}
          >
            {label}
          </span>
        );
      }

      if (field === "TreatmentType") {
        return (
          <TreatmentStatusControl
            value={current}
            disabled={!ctx.studyId}
            busy={rowBusy}
            onChange={(treatmentType) => onClassify(row, treatmentType)}
          />
        );
      }

      if (field === "NeutralDisplayName") {
        const labelValue =
          labelDrafts[id] ??
          assigned?.NeutralDisplayName ??
          assigned?.neutralDisplayName ??
          "";
        return (
          <SiteFacingLabelEditor
            value={assigned ? labelValue : ""}
            disabled={!ctx.studyId || !assigned}
            busy={rowBusy}
            onSave={(next) => onSaveLabel(row, next)}
          />
        );
      }

      return (
        row[field] ??
        row[field?.charAt?.(0)?.toLowerCase() + field?.slice?.(1)] ??
        "—"
      );
    },
    [
      assignmentByImpId,
      busy,
      savingImpId,
      ctx.studyId,
      labelDrafts,
      openProductDetail,
    ],
  );

  const renderKitMapCell = useCallback(
    (row, field) => {
      if (field === "KitNumber") {
        const kit = String(row.KitNumber || row.kitNumber || "").trim() || "—";
        return kit;
      }
      if (field === "InventoryStatus") {
        return renderReceiptStatusBadge(
          row.InventoryStatus ||
            row.inventoryStatus ||
            row.PhysicalStatus ||
            row.physicalStatus ||
            "",
        );
      }
      if (field === "AssignImp") {
        const kit = String(row.KitNumber || row.kitNumber || "").trim();
        const value = kitImpByNumber[kit] || "";
        return (
          <KitAssignImpControl
            value={value}
            options={studyProductOptions}
            disabled={!studyProductOptions.length}
            busy={busy}
            onSelect={(nextValue, { applyAll } = {}) => {
              if (applyAll) {
                const next = {};
                pendingKitsForBatch.forEach((r) => {
                  const k = String(r.KitNumber || r.kitNumber || "").trim();
                  if (k) next[k] = nextValue || "";
                });
                setKitImpByNumber(next);
                return;
              }
              setKitImpByNumber((prev) => ({
                ...prev,
                [kit]: nextValue || "",
              }));
            }}
          />
        );
      }
      if (field === "TreatmentType") {
        return <TreatmentBadge value={row.TreatmentType || row.treatmentType} />;
      }
      if (field === "DisplayStatus") {
        const mappingStatus = String(
          row.MappingStatus || row.mappingStatus || "",
        ).trim();
        const hasMap = Boolean(
          String(row.StudyImpProductId || row.studyImpProductId || "").trim(),
        );
        const value =
          mappingStatus === "RequiresSponsorResolution" || !hasMap
            ? "RequiresSponsorResolution"
            : "Mapped";
        return <MappingBadge value={value} />;
      }
      if (field === "ExpiryDate") {
        const raw = row.ExpiryDate || row.expiryDate;
        if (!raw) return "—";
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return String(raw);
        return d.toLocaleDateString();
      }
      return (
        row[field] ??
        row[field?.charAt?.(0)?.toLowerCase() + field?.slice?.(1)] ??
        "—"
      );
    },
    [busy, kitImpByNumber, pendingKitsForBatch, studyProductOptions],
  );

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page epsf-setup-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: crumbLabel },
          ]}
          actions={[
            {
              icon: "fas fa-refresh",
              label: "Refresh",
              onClick: () => loadAll(),
            },
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}
        {success ? <p className="epsf-list-page__ok">{success}</p> : null}

        {!ctx.studyId ? (
          <p className="epsf-list-page__hint">
            Select a study in the breadcrumb bar to configure treatment setup.
          </p>
        ) : null}

        {tab === "products" && canTreatmentSetup ? (
          <div className="epsf-setup-body">
            {ctx.studyId ? (
              <div
                className="epsf-return-summary"
                aria-label="Treatment summary"
              >
                <button
                  type="button"
                  className={`epsf-return-summary__item epsf-setup-summary__unassigned epsf-setup-kpi${
                    kpiFilterKey === "UNASSIGNED" ? " is-selected" : ""
                  }`}
                  onClick={() => toggleKpiFilter("Unassigned")}
                  title={
                    kpiFilterKey === "UNASSIGNED"
                      ? "Clear filter"
                      : "Show unassigned IMP only"
                  }
                  aria-pressed={kpiFilterKey === "UNASSIGNED"}
                >
                  <span className="epsf-return-summary__label">
                    Assigned / Unassigned
                  </span>
                  <span className="epsf-return-summary__value">
                    {assignedCount}
                    <span className="epsf-setup-kpi-slash">/</span>
                    {unassignedCount}
                  </span>
                </button>
                <button
                  type="button"
                  className={`epsf-return-summary__item epsf-setup-summary__active epsf-setup-kpi${
                    kpiFilterKey === "ACTIVE" ? " is-selected" : ""
                  }`}
                  onClick={() => toggleKpiFilter("ACTIVE")}
                  title={
                    kpiFilterKey === "ACTIVE"
                      ? "Clear filter"
                      : "Show Active only"
                  }
                  aria-pressed={kpiFilterKey === "ACTIVE"}
                >
                  <span className="epsf-return-summary__label">Active</span>
                  <span className="epsf-return-summary__value">
                    {treatmentSummary.active}
                  </span>
                </button>
                <button
                  type="button"
                  className={`epsf-return-summary__item epsf-setup-summary__placebo epsf-setup-kpi${
                    kpiFilterKey === "PLACEBO" ? " is-selected" : ""
                  }`}
                  onClick={() => toggleKpiFilter("PLACEBO")}
                  title={
                    kpiFilterKey === "PLACEBO"
                      ? "Clear filter"
                      : "Show Placebo only"
                  }
                  aria-pressed={kpiFilterKey === "PLACEBO"}
                >
                  <span className="epsf-return-summary__label">Placebo</span>
                  <span className="epsf-return-summary__value">
                    {treatmentSummary.placebo}
                  </span>
                </button>
                <button
                  type="button"
                  className={`epsf-return-summary__item epsf-setup-summary__comparator epsf-setup-kpi${
                    kpiFilterKey === "COMPARATOR" ? " is-selected" : ""
                  }`}
                  onClick={() => toggleKpiFilter("COMPARATOR")}
                  title={
                    kpiFilterKey === "COMPARATOR"
                      ? "Clear filter"
                      : "Show Comparator only"
                  }
                  aria-pressed={kpiFilterKey === "COMPARATOR"}
                >
                  <span className="epsf-return-summary__label">
                    Comparator
                  </span>
                  <span className="epsf-return-summary__value">
                    {treatmentSummary.comparator}
                  </span>
                </button>
              </div>
            ) : null}

            <div className="epsf-report-wrap epsf-setup-page__table">
              <ReportTable
                rows={filteredTreatmentRows}
                fieldOptions={treatmentFieldOptions}
                defaultColumns={treatmentDefaultColumns}
                getFieldLabel={(field) =>
                  treatmentFieldOptions.find((x) => x.key === field)?.label ||
                  field
                }
                renderCell={renderTreatmentSetupCell}
                renderColumnSearch={renderColumnSearch}
                matchColumnSearch={matchColumnSearch}
                emptyMessage={
                  !ctx.studyId
                    ? "Select study and site in the breadcrumb bar."
                    : "No organisation IMP products yet. Add them under IMP Products first."
                }
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                persistLayoutKey="epsf-imp-treatment-setup-v3"
                columnSearchMode="client"
              />
            </div>

            {detailRow
              ? createPortal(
                  <>
                    <div
                      className="modal-backdrop fade show"
                      onClick={closeProductDetail}
                    />
                    <div
                      className="modal fade show d-block epsf-modal epsf-setup-detail-modal"
                      tabIndex={-1}
                      role="dialog"
                      aria-modal="true"
                      aria-label="IMP study treatment details"
                    >
                      <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                          <div className="modal-header">
                            <div>
                              <h5 className="modal-title mb-0">
                                {productNameOf(detailRow) || "IMP details"}
                              </h5>
                              <div className="epsf-setup-detail-modal__code">
                                {productCodeOf(detailRow) || "—"}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-close"
                              aria-label="Close"
                              onClick={closeProductDetail}
                            />
                          </div>
                          <div className="modal-body">
                            {(() => {
                              const id = impIdOf(detailRow);
                              const assigned = assignmentByImpId.get(id);
                              const current =
                                assigned?.TreatmentType ||
                                assigned?.treatmentType ||
                                "";
                              const rowBusy = busy && savingImpId === id;
                              const { dosage, strength } =
                                getDosageAndStrength(detailRow);
                              const tempLabel =
                                formatStorageTempRange(
                                  detailRow.StorageTempMin ??
                                    detailRow.storageTempMin,
                                  detailRow.StorageTempMax ??
                                    detailRow.storageTempMax,
                                  detailRow.StorageTempUnit ||
                                    detailRow.storageTempUnit,
                                  detailRow.StorageCondition ||
                                    detailRow.storageCondition ||
                                    "",
                                ) || "—";
                              return (
                                <>
                                  <div className="epsf-setup-detail-grid">
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Dosage form
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {dosage || "—"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Strength
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {strength || "—"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Route
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {detailRow.Route ||
                                          detailRow.route ||
                                          "—"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Manufacturer
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {detailRow.Manufacturer ||
                                          detailRow.manufacturer ||
                                          "—"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Storage temperature
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {tempLabel}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="epsf-setup-detail-grid__label">
                                        Tracking
                                      </div>
                                      <div className="epsf-setup-detail-grid__value">
                                        {String(
                                          detailRow.InventoryTrackingMethod ||
                                            detailRow.inventoryTrackingMethod ||
                                            "IndividualKit",
                                        ) === "IndividualKit"
                                          ? "Individual Kit"
                                          : "Batch / Quantity"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="epsf-setup-detail-edit">
                                    <div className="epsf-form-field">
                                      <label className="form-label">
                                        Treatment
                                      </label>
                                      <TreatmentStatusControl
                                        value={current}
                                        disabled={!ctx.studyId}
                                        busy={rowBusy}
                                        onChange={(treatmentType) =>
                                          onClassify(detailRow, treatmentType)
                                        }
                                      />
                                    </div>
                                    <div className="epsf-form-field">
                                      <label className="form-label">
                                        Site-facing label
                                      </label>
                                      <div className="epsf-setup-detail-edit__label-row">
                                        <input
                                          className="form-control"
                                          value={detailLabelDraft}
                                          maxLength={200}
                                          disabled={
                                            !ctx.studyId ||
                                            rowBusy ||
                                            !assigned
                                          }
                                          placeholder={
                                            assigned
                                              ? "e.g. Study Medication"
                                              : "Assign a treatment first"
                                          }
                                          onChange={(e) =>
                                            setDetailLabelDraft(e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              onSaveLabel(
                                                detailRow,
                                                detailLabelDraft,
                                              );
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-primary"
                                          disabled={
                                            !ctx.studyId ||
                                            rowBusy ||
                                            !assigned
                                          }
                                          onClick={() =>
                                            onSaveLabel(
                                              detailRow,
                                              detailLabelDraft,
                                            )
                                          }
                                        >
                                          {rowBusy ? "Saving…" : "Save"}
                                        </button>
                                      </div>
                                      {!assigned ? (
                                        <p className="form-text mb-0 mt-1">
                                          Assign a treatment to enable the
                                          site-facing label.
                                        </p>
                                      ) : null}
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-light"
                              onClick={closeProductDetail}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body,
                )
              : null}
          </div>
        ) : null}

        {tab === "kits" && canKitTreatmentManage ? (
          <>
            <div className="epsf-kit-map-split" aria-label="Pending mapping">
                <aside className="epsf-kit-map-split__batches">
                  <div className="epsf-kit-map-batch-pane__head">
                    <div className="epsf-kit-map-batch-pane__head-top">
                      <h2 className="epsf-kit-map-batch-pane__title">
                        Batches
                      </h2>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={openPrepareModal}
                        disabled={!ctx.studyId}
                      >
                        Add new batch
                      </button>
                    </div>
                    <div className="epsf-kit-map-batch-pane__search-row">
                      <input
                        type="search"
                        className="form-control form-control-sm epsf-kit-map-batch-pane__search"
                        placeholder="Search batch, expiry, status"
                        value={batchListQuery}
                        onChange={(e) => setBatchListQuery(e.target.value)}
                        aria-label="Search batches"
                      />
                    </div>
                  </div>
                  {filteredBatchOptions.length ? (
                    <div className="epsf-kit-map-batch-pane__body">
                      {filteredBatchOptions.map((opt) => {
                        const selected = pendingBatchKey === opt.value;
                        return (
                          <div
                            key={opt.value}
                            className={`epsf-kit-map-batch-row${
                              selected ? " is-selected" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="epsf-kit-map-batch-row__btn"
                              onClick={() => setPendingBatchKey(opt.value)}
                              title={opt.batchNumber || "(No batch / lot)"}
                            >
                              <span className="epsf-kit-map-batch-row__text">
                                <span className="epsf-kit-map-batch-row__line1">
                                  <span className="epsf-kit-map-batch-row__name">
                                    {opt.batchNumber || "(No batch / lot)"}
                                  </span>
                                </span>
                                <span className="epsf-kit-map-batch-row__meta">
                                  <span
                                    className={`epsf-kit-map-batch-row__chip epsf-kit-map-batch-row__chip--${
                                      opt.expiryTone || "none"
                                    }`}
                                    title={
                                      opt.expiryTone === "near"
                                        ? `Expires within ${NEAR_EXPIRY_DAYS} days`
                                        : opt.expiryTone === "expired"
                                          ? "Batch expired"
                                          : undefined
                                    }
                                  >
                                    {expiryChipLabel(opt.expiryDate)}
                                  </span>
                                  <span
                                    className={`epsf-kit-map-batch-row__chip epsf-kit-map-batch-row__chip--${
                                      opt.mappingTone || "none"
                                    }`}
                                    title={opt.mappingLabel}
                                  >
                                    {opt.mappedCount}/{opt.kitCount} Mapped
                                  </span>
                                </span>
                              </span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="epsf-kit-map-split__empty">
                      {pendingBatchOptions.length
                        ? "No batches match this search."
                        : "No batches for this study yet."}
                    </p>
                  )}
                </aside>

                <section className="epsf-kit-map-split__detail">
                  {!pendingBatchKey ? (
                    <div className="epsf-kit-map-split__placeholder">
                      Select a batch on the left to see kits and assign IMP.
                    </div>
                  ) : (
                    <>
                      <div className="epsf-kit-map-split__detail-head">
                        <div>
                          <h2 className="epsf-kit-map-split__title">
                            {selectedPendingBatch?.batchNumber ||
                              "(No batch / lot)"}
                          </h2>
                          <p className="epsf-kit-map-split__hint mb-0">
                            {selectedPendingBatch?.expiryDate
                              ? `Expiry ${selectedPendingBatch.expiryDate}`
                              : "No expiry"}
                            {" · "}
                            {selectedPendingBatch?.mappedCount || 0}/
                            {pendingKitsForBatch.length} Mapped
                            {selectedPendingBatch?.mappingLabel
                              ? ` · ${selectedPendingBatch.mappingLabel}`
                              : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={busy || !canSavePendingMapping}
                          title={
                            canSavePendingMapping
                              ? "Save IMP assignments for this batch"
                              : "Assign or change IMP on at least one kit to save"
                          }
                          onClick={onMapPendingBatch}
                        >
                          {busy ? "Saving…" : "Save mapping"}
                        </button>
                      </div>

                      <div className="epsf-report-wrap epsf-kit-map-split__table">
                        <ReportTable
                          rows={pendingKitsForBatch}
                          fieldOptions={pendingKitFieldOptions}
                          defaultColumns={pendingKitDefaultColumns}
                          getFieldLabel={(field) =>
                            pendingKitFieldOptions.find((x) => x.key === field)
                              ?.label || field
                          }
                          renderCell={renderKitMapCell}
                          emptyMessage="No pending kits in this batch."
                          pageSizeOptions={PAGE_SIZE_OPTIONS}
                          persistLayoutKey="epsf-imp-kit-map-pending-v8"
                          columnSearchMode="client"
                        />
                      </div>
                    </>
                  )}
                </section>
            </div>

            {prepareModalOpen
              ? createPortal(
                  <div
                    className="modal fade show d-block epsf-modal epsf-receipt-modal epsf-prepare-batch-modal"
                    tabIndex={-1}
                    aria-modal="true"
                  >
                    <div className="modal-dialog modal-xl epsf-receipt-modal__dialog">
                      <div className="modal-content epsf-receipt-modal__content">
                        <form
                          onSubmit={onPrepareBatch}
                          noValidate
                          className="epsf-receipt-modal__form"
                        >
                          <div className="modal-header py-2">
                            <h5 className="modal-title mb-0">Add new batch</h5>
                            <button
                              type="button"
                              className="btn-close"
                              aria-label="Close"
                              onClick={closePrepareModal}
                              disabled={busy}
                            />
                          </div>
                          <div className="modal-body epsf-receipt-modal__body">
                            <div className="row g-3 epsf-receipt-modal__layout epsf-receipt-modal__layout--triple">
                              <div className="col-12 col-lg-8 epsf-receipt-modal__pane">
                                <h3 className="epsf-receipt-section-label">
                                  Kits <span className="mandatory">*</span>
                                </h3>
                                <section className="epsf-receipt-section epsf-receipt-section--kits">
                                  <div className="epsf-receipt-section__body">
                                    <div className="epsf-kit-table-input epsf-kit-table-input--with-imp">
                                      {prepareHint ? (
                                        <div
                                          className="epsf-kit-table-input__hint"
                                          role="status"
                                        >
                                          {prepareHint}
                                        </div>
                                      ) : null}

                                      <div className="epsf-kit-table-input__add-bar">
                                        <input
                                          className="form-control epsf-kit-table-input__control"
                                          value={prepareDraftKit}
                                          placeholder="Enter kit number"
                                          disabled={busy}
                                          autoComplete="off"
                                          aria-label="Add kit number"
                                          onChange={(e) =>
                                            setPrepareDraftKit(e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            if (
                                              e.key === "Enter" ||
                                              e.key === "," ||
                                              e.key === ";"
                                            ) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              addPrepareKitRow();
                                            }
                                          }}
                                          onPaste={(e) => {
                                            const text =
                                              e.clipboardData?.getData("text");
                                            if (
                                              !text ||
                                              !/[\n,;\t]/.test(text)
                                            ) {
                                              return;
                                            }
                                            e.preventDefault();
                                            addPrepareKitsFromList([
                                              ...parseKitList(text),
                                              ...parseKitList(prepareDraftKit),
                                            ]);
                                            setPrepareDraftKit("");
                                          }}
                                        />
                                        <div className="epsf-kit-table-input__add-expiry">
                                          <SegmentDatePicker
                                            selected={prepareDraftExpiry}
                                            onChange={(date) =>
                                              setPrepareDraftExpiry(date)
                                            }
                                            isClearable
                                            disabled={busy}
                                            aria-label="Expiry for new kit"
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary epsf-kit-table-input__add-btn"
                                          disabled={
                                            busy || !prepareDraftKit.trim()
                                          }
                                          onClick={addPrepareKitRow}
                                        >
                                          Add
                                        </button>
                                      </div>

                                      <div
                                        className="epsf-kit-table-input__divider"
                                        aria-hidden="true"
                                      />

                                      <div
                                        className="epsf-kit-table-input__cols"
                                        aria-hidden={
                                          !(prepareForm.rows || []).length
                                        }
                                      >
                                        <span>Kit number</span>
                                        <span>Expiry</span>
                                        <span>Assign IMP</span>
                                        <span />
                                      </div>

                                      <div
                                        className="epsf-kit-table-input__list"
                                        role="list"
                                      >
                                        {(prepareForm.rows || []).length ? (
                                          prepareForm.rows.map((row) => (
                                            <div
                                              key={row.id}
                                              className="epsf-kit-table-input__row"
                                              role="listitem"
                                            >
                                              <div className="epsf-kit-table-input__kit">
                                                <input
                                                  className="form-control epsf-kit-table-input__control"
                                                  value={row.kitNumber}
                                                  disabled={busy}
                                                  maxLength={100}
                                                  aria-label={`Kit number ${row.kitNumber}`}
                                                  onChange={(e) => {
                                                    const nextVal =
                                                      e.target.value;
                                                    setPrepareForm((f) => ({
                                                      ...f,
                                                      rows: (f.rows || []).map(
                                                        (r) =>
                                                          r.id === row.id
                                                            ? {
                                                                ...r,
                                                                kitNumber:
                                                                  nextVal,
                                                              }
                                                            : r,
                                                      ),
                                                    }));
                                                  }}
                                                />
                                              </div>
                                              <div className="epsf-kit-table-input__expiry">
                                                <SegmentDatePicker
                                                  selected={dateFromInputValue(
                                                    row.expiryDate,
                                                  )}
                                                  onChange={(date) => {
                                                    const next =
                                                      toDateInputValue(date);
                                                    setPrepareForm((f) => ({
                                                      ...f,
                                                      rows: (f.rows || []).map(
                                                        (r) =>
                                                          r.id === row.id
                                                            ? {
                                                                ...r,
                                                                expiryDate:
                                                                  next,
                                                              }
                                                            : r,
                                                      ),
                                                    }));
                                                  }}
                                                  isClearable
                                                  disabled={busy}
                                                  aria-label={`Expiry for ${row.kitNumber}`}
                                                />
                                              </div>
                                              <div className="epsf-kit-table-input__imp">
                                                <KitAssignImpControl
                                                  value={
                                                    row.studyImpProductId || ""
                                                  }
                                                  options={studyProductOptions}
                                                  disabled={
                                                    !studyProductOptions.length
                                                  }
                                                  busy={busy}
                                                  onSelect={(
                                                    nextValue,
                                                    { applyAll } = {},
                                                  ) => {
                                                    setPrepareForm((f) => ({
                                                      ...f,
                                                      rows: (f.rows || []).map(
                                                        (r) => {
                                                          if (applyAll) {
                                                            return {
                                                              ...r,
                                                              studyImpProductId:
                                                                nextValue || "",
                                                            };
                                                          }
                                                          if (r.id !== row.id)
                                                            return r;
                                                          return {
                                                            ...r,
                                                            studyImpProductId:
                                                              nextValue || "",
                                                          };
                                                        },
                                                      ),
                                                    }));
                                                  }}
                                                />
                                              </div>
                                              <div className="epsf-kit-table-input__actions">
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-secondary epsf-kit-table-input__remove-btn"
                                                  disabled={busy}
                                                  aria-label={`Remove ${row.kitNumber}`}
                                                  onClick={() =>
                                                    setPrepareForm((f) => ({
                                                      ...f,
                                                      rows: (
                                                        f.rows || []
                                                      ).filter(
                                                        (r) => r.id !== row.id,
                                                      ),
                                                    }))
                                                  }
                                                >
                                                  <i
                                                    className="fas fa-trash-alt"
                                                    aria-hidden="true"
                                                  />
                                                </button>
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="epsf-kit-table-input__empty">
                                            No kits added yet. Type a kit
                                            number above and press Enter or
                                            Add.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </section>
                              </div>

                              <div className="col-12 col-lg-4 epsf-receipt-modal__pane epsf-prepare-batch-modal__side">
                                <h3 className="epsf-receipt-section-label">
                                  Batch details
                                </h3>
                                <section className="epsf-receipt-section epsf-receipt-section--details">
                                  <div className="epsf-receipt-section__body">
                                    <div className="epsf-prepare-batch-modal__fields">
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Batch / Lot{" "}
                                          <span className="mandatory">*</span>
                                        </label>
                                        <input
                                          className="form-control"
                                          value={prepareForm.batchNumber}
                                          onChange={(e) =>
                                            setPrepareForm((f) => ({
                                              ...f,
                                              batchNumber: e.target.value,
                                            }))
                                          }
                                          placeholder="e.g. LOT-2026-001"
                                          maxLength={100}
                                          disabled={busy}
                                        />
                                      </div>
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Batch expiry{" "}
                                          <span className="mandatory">*</span>
                                        </label>
                                        <SegmentDatePicker
                                          selected={dateFromInputValue(
                                            prepareForm.expiryDate,
                                          )}
                                          onChange={(date) => {
                                            const next = toDateInputValue(date);
                                            setPrepareForm((f) => ({
                                              ...f,
                                              expiryDate: next,
                                            }));
                                            setPrepareDraftExpiry(
                                              dateFromInputValue(next),
                                            );
                                          }}
                                          isClearable
                                          disabled={busy}
                                          aria-label="Batch expiry"
                                        />
                                      </div>
                                      <div className="epsf-form-field">
                                        <label className="form-label">
                                          Shipment / reference
                                        </label>
                                        <input
                                          className="form-control"
                                          value={prepareForm.shipmentReference}
                                          onChange={(e) =>
                                            setPrepareForm((f) => ({
                                              ...f,
                                              shipmentReference: e.target.value,
                                            }))
                                          }
                                          maxLength={100}
                                          placeholder="e.g. SHIP-10045"
                                          disabled={busy}
                                        />
                                      </div>
                                      <div className="epsf-form-field mb-0">
                                        <label className="form-label">
                                          Temperature (°C)
                                        </label>
                                        <input
                                          className="form-control"
                                          value={prepareForm.temperature}
                                          onChange={(e) =>
                                            setPrepareForm((f) => ({
                                              ...f,
                                              temperature: e.target.value,
                                            }))
                                          }
                                          placeholder="Optional"
                                          disabled={busy}
                                          inputMode="decimal"
                                        />
                                      </div>
                                    </div>
                                    <p className="form-text mb-0 mt-1">
                                      Batch expiry is the default for new kits.
                                      You can override it per kit in the table.
                                    </p>
                                  </div>
                                </section>

                                <h3 className="epsf-receipt-section-label">
                                  Add kits
                                </h3>
                                <section className="epsf-receipt-section epsf-receipt-section--add">
                                  <div className="epsf-receipt-section__body">
                                    <div className="mb-3">
                                      {!phoneScan ? (
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm w-100"
                                          disabled={
                                            busy ||
                                            phoneScanBusy ||
                                            !ctx.studyId
                                          }
                                          onClick={startPreparePhoneScan}
                                        >
                                          {phoneScanBusy
                                            ? "Starting…"
                                            : "Scan with phone"}
                                        </button>
                                      ) : (
                                        <div className="epsf-phone-scan-panel">
                                          <div className="d-flex justify-content-between align-items-start gap-2">
                                            <strong>Phone scanner</strong>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-secondary"
                                              onClick={stopPreparePhoneScan}
                                            >
                                              Stop
                                            </button>
                                          </div>
                                          <div
                                            className="epsf-phone-scan-panel__code"
                                            aria-label="Short scan code"
                                          >
                                            {phoneScan.shortCode ||
                                              phoneScan.token}
                                          </div>
                                          {phoneScan.url ? (
                                            <>
                                              <img
                                                className="epsf-phone-scan-panel__qr"
                                                alt="QR code for phone scanner"
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(phoneScan.url)}`}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary w-100 mt-2"
                                                onClick={() => {
                                                  navigator.clipboard
                                                    ?.writeText(phoneScan.url)
                                                    .then(() =>
                                                      setPhoneScanHint(
                                                        "Phone link copied",
                                                      ),
                                                    )
                                                    .catch(() => {});
                                                }}
                                              >
                                                Copy link
                                              </button>
                                            </>
                                          ) : null}
                                          {phoneScanHint ? (
                                            <div className="form-text text-success mt-1 mb-0">
                                              {phoneScanHint}
                                            </div>
                                          ) : null}
                                        </div>
                                      )}
                                      {!phoneScan && phoneScanHint ? (
                                        <div className="form-text text-danger mt-1 mb-0">
                                          {phoneScanHint}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="epsf-form-field mb-0">
                                      <label className="form-label">
                                        Paste kit numbers
                                      </label>
                                      <textarea
                                        className="form-control"
                                        rows={5}
                                        disabled={busy}
                                        value={preparePasteBulk}
                                        placeholder={
                                          "Comma or line separated\nKIT-001, KIT-002\nKIT-003"
                                        }
                                        onChange={(e) =>
                                          setPreparePasteBulk(e.target.value)
                                        }
                                        aria-label="Paste kit numbers"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary w-100 mt-2"
                                        disabled={
                                          busy ||
                                          !parseKitList(preparePasteBulk)
                                            .length
                                        }
                                        onClick={applyPreparePasteBulk}
                                      >
                                        Add pasted kits
                                      </button>
                                    </div>
                                  </div>
                                </section>
                              </div>
                            </div>
                          </div>
                          <div className="modal-footer py-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={closePrepareModal}
                              disabled={busy}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={
                                busy ||
                                !ctx.studyId ||
                                !String(prepareForm.batchNumber || "").trim() ||
                                !(prepareForm.rows || []).length
                              }
                            >
                              {busy ? "Saving…" : "Save mapping"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>,
                  document.body,
                )
              : null}
          </>
        ) : null}

        {tab === "products" && !canTreatmentSetup ? (
          <p className="epsf-list-page__error">
            You do not have permission for study IMP treatment setup.
          </p>
        ) : null}
        {tab === "kits" && !canKitTreatmentManage ? (
          <p className="epsf-list-page__error">
            You do not have permission for kit treatment mapping.
          </p>
        ) : null}
      </div>
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
